import React from 'react'
import { Container, Typography, Stack, Link, Divider, List, ListItem, ListItemText } from '@mui/material'
import Layout from '../components/Layout'
import { useTranslation } from 'react-i18next'

const Datenschutz: React.FC = () => {
  const { t } = useTranslation()

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          {t('privacy')}
        </Typography>
        <Stack spacing={4}>
          <Typography variant="body2">
            Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Alle
            personenbezogenen Informationen werden vertraulich und
            entsprechend den gesetzlichen Datenschutzvorschriften (insbesondere
            DSGVO und BDSG) sowie dieser Datenschutzerklärung verarbeitet.
          </Typography>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Verantwortlicher
            </Typography>
            <Typography>
              Webmix-IT – E-Mail:{' '}
              <Link href="mailto:hallo@webmix-it.de">hallo@webmix-it.de</Link>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bei Fragen zu dieser Erklärung oder zur Ausübung Ihrer Rechte
              wenden Sie sich bitte jederzeit an uns.
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Hosting und Zugriffsdaten
            </Typography>
            <Typography variant="body2">
              Unser Angebot wird auf Servern innerhalb der Europäischen Union
              betrieben. Bei jedem Zugriff auf unsere Website erheben und
              speichern wir automatisch Informationen in sogenannten
              Server-Logfiles. Dazu gehören IP-Adresse (gekürzt oder
              pseudonymisiert), Datum und Uhrzeit des Abrufs, URL der abgerufenen
              Datei, Menge der übertragenen Daten, Meldung über erfolgreichen
              Abruf, Browsertyp und -version, Betriebssystem sowie Referrer-URL.
              Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f
              DSGVO und dient der Sicherstellung eines störungsfreien
              Betriebs, der Systemsicherheit sowie der statistischen
              Auswertung. Logfiles werden in der Regel nach 14 Tagen gelöscht,
              sofern keine weitere Aufbewahrung zu Beweiszwecken erforderlich
              ist.
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Kontaktaufnahme
            </Typography>
            <Typography variant="body2">
              Wenn Sie uns per E-Mail oder Formular kontaktieren, verarbeiten
              wir Ihre Angaben (Name, Kontaktinformationen, Nachricht) zur
              Bearbeitung Ihrer Anfrage. Die Datenverarbeitung erfolgt gemäß
              Art. 6 Abs. 1 lit. b DSGVO, sofern die Anfrage auf den Abschluss
              eines Vertrages abzielt, oder nach Art. 6 Abs. 1 lit. f DSGVO auf
              Grundlage unseres berechtigten Interesses an der Bearbeitung von
              Anfragen. Ihre Daten werden gelöscht, sobald Ihre Anfrage
              abschließend bearbeitet wurde und keine gesetzlichen
              Aufbewahrungspflichten entgegenstehen.
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Analyse-Tools und Cookies
            </Typography>
            <Typography variant="body2">
              Wir verwenden nur dann Cookies oder Analyse-Dienste, wenn Sie
              uns hierzu Ihre Einwilligung erteilen (Art. 6 Abs. 1 lit. a
              DSGVO). Nähere Informationen zu einzelnen Diensten, deren
              Funktionsweise sowie die Gültigkeitsdauer der jeweiligen Cookies
              erhalten Sie direkt im Einwilligungsdialog. Sie können Ihre
              Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen,
              indem Sie Ihre Cookie-Einstellungen anpassen oder uns eine kurze
              Nachricht schicken.
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Auftragsverarbeitung und Datenübermittlung
            </Typography>
            <Typography variant="body2">
              Sofern wir externe Dienstleister mit der Verarbeitung von
              Daten beauftragen, erfolgt dies ausschließlich auf Grundlage von
              Art. 28 DSGVO. Eine Übermittlung in Drittländer findet nur
              statt, wenn hierfür entweder ein Angemessenheitsbeschluss der
              EU-Kommission vorliegt oder geeignete Garantien (z. B.
              Standardvertragsklauseln) vereinbart wurden.
            </Typography>
          </Stack>

          <Divider flexItem />

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Ihre Rechte
            </Typography>
            <Typography variant="body2">
              Sie haben jederzeit das Recht, die folgenden Betroffenenrechte
              wahrzunehmen:
            </Typography>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemText
                  primary="Auskunft"
                  secondary="Sie erhalten auf Anfrage eine Übersicht über die von uns verarbeiteten personenbezogenen Daten (Art. 15 DSGVO)."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Berichtigung"
                  secondary="Sie können die Korrektur unrichtiger oder unvollständiger Daten verlangen (Art. 16 DSGVO)."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Löschung"
                  secondary="Sie können die Löschung Ihrer Daten verlangen, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen (Art. 17 DSGVO)."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Einschränkung der Verarbeitung"
                  secondary="Sie können die Verarbeitung Ihrer Daten in bestimmten Fällen einschränken lassen (Art. 18 DSGVO)."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Datenübertragbarkeit"
                  secondary="Sie haben das Recht, die Sie betreffenden Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten (Art. 20 DSGVO)."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary="Widerspruch"
                  secondary="Sie können der Verarbeitung Ihrer Daten aus Gründen, die sich aus Ihrer besonderen Situation ergeben, widersprechen (Art. 21 DSGVO)."
                />
              </ListItem>
            </List>
            <Typography variant="body2">
              Darüber hinaus besteht ein Beschwerderecht bei der zuständigen
              Datenschutz-Aufsichtsbehörde. In Bayern ist dies beispielsweise
              das Bayerische Landesamt für Datenschutzaufsicht (BayLDA).
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Widerruf von Einwilligungen
            </Typography>
            <Typography variant="body2">
              Bereits erteilte Einwilligungen können Sie jederzeit mit Wirkung
              für die Zukunft widerrufen. Die Rechtmäßigkeit der aufgrund der
              Einwilligung bis zum Widerruf erfolgten Verarbeitung bleibt
              unberührt.
            </Typography>
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Aktualität und Änderungen dieser Datenschutzerklärung
            </Typography>
            <Typography variant="body2">
              Wir passen diese Datenschutzerklärung an, sobald Änderungen an
              unseren Verarbeitungsprozessen oder an den gesetzlichen
              Vorgaben dies erforderlich machen. Die aktuelle Fassung ist
              jederzeit auf dieser Seite abrufbar. Stand: Oktober 2023.
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Layout>
  )
}

export default Datenschutz
